import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import {
  Card, Select, Button, Row, Col, message, Empty, Spin, Space, Modal, Typography,
  ColorPicker, Tooltip, Input, Segmented, Tag,
} from 'antd';
import {
  DownloadOutlined, TeamOutlined, LeftOutlined, UserOutlined, BgColorsOutlined,
  DragOutlined, ReloadOutlined, EditOutlined, HighlightOutlined, UndoOutlined,
  DeleteOutlined, SaveOutlined, FolderOpenOutlined, WhatsAppOutlined,
  FontSizeOutlined, CloseOutlined, ExpandOutlined, CompressOutlined,
  PlusOutlined, MinusOutlined, InfoCircleOutlined,
} from '@ant-design/icons';
import * as htmlToImage from 'html-to-image';
import api from '../../services/api';
import { getActiveTeamId } from '../../utils/activeTeam';

const { Text, Title } = Typography;

// Formações por tipo de jogo. Cada slot tem { role, x%, y%, key } — coords
// relativas ao container do campo. y=0 eh o gol de baixo (defesa), y=100
// eh o ataque no topo.
const FORMATIONS = {
  salao: {
    label: 'Salão (Futsal, 5)',
    playersTotal: 5,
    schemes: {
      '1-2-1':   [gk(50,6), ['fixo', 50, 30], ['ala',20,55], ['ala',80,55], ['pivo',50,82]],
      '2-2':     [gk(50,6), ['zag',30,32], ['zag',70,32], ['ata',30,70], ['ata',70,70]],
      '3-1':     [gk(50,6), ['zag',50,28], ['ala',20,52], ['ala',80,52], ['ata',50,80]],
      '4-0':     [gk(50,6), ['zag',30,28], ['zag',70,28], ['ata',30,70], ['ata',70,70]],
    },
  },
  society: {
    label: 'Society (7)',
    playersTotal: 7,
    schemes: {
      '2-3-1': [gk(50,6), ['zag',30,25], ['zag',70,25], ['meia',20,52], ['meia',50,52], ['meia',80,52], ['ata',50,82]],
      '3-2-1': [gk(50,6), ['zag',25,25], ['zag',50,25], ['zag',75,25], ['meia',35,52], ['meia',65,52], ['ata',50,82]],
      '2-2-2': [gk(50,6), ['zag',30,25], ['zag',70,25], ['meia',30,52], ['meia',70,52], ['ata',30,82], ['ata',70,82]],
      '3-3':   [gk(50,6), ['zag',25,25], ['zag',50,25], ['zag',75,25], ['ata',25,68], ['ata',50,68], ['ata',75,68]],
    },
  },
  campo: {
    label: 'Campo (11)',
    playersTotal: 11,
    schemes: {
      '4-4-2':   [gk(50,5), ['zag',15,22], ['zag',37,22], ['zag',63,22], ['zag',85,22], ['meia',15,48], ['meia',37,48], ['meia',63,48], ['meia',85,48], ['ata',35,80], ['ata',65,80]],
      '4-3-3':   [gk(50,5), ['zag',15,22], ['zag',37,22], ['zag',63,22], ['zag',85,22], ['meia',28,48], ['meia',50,48], ['meia',72,48], ['ata',20,80], ['ata',50,80], ['ata',80,80]],
      '4-2-3-1': [gk(50,5), ['zag',15,22], ['zag',37,22], ['zag',63,22], ['zag',85,22], ['meia',35,44], ['meia',65,44], ['meia',25,64], ['meia',50,64], ['meia',75,64], ['ata',50,84]],
      '3-5-2':   [gk(50,5), ['zag',25,22], ['zag',50,22], ['zag',75,22], ['meia',10,50], ['meia',30,50], ['meia',50,50], ['meia',70,50], ['meia',90,50], ['ata',35,80], ['ata',65,80]],
    },
  },
  // Custom: quantidade + esquema livre. Ao selecionar, user define quantos
  // em campo (2-15) e como distribuir (ex: '2-3-1' = 2 zag / 3 meia / 1 ata).
  // A função buildCustomSlots (abaixo) gera as coords em grid.
  custom: {
    label: 'Personalizado',
    playersTotal: 0,
    schemes: { 'Livre': [gk(50,6)] }, // seed inicial só com goleiro
  },
};
function gk(x, y) { return ['gol', x, y]; }

// Gera slots pra esquema custom "N linhas separadas por -". Ex: "3-3-2" com
// includeGk=true → gol + 3 zag + 3 meia + 2 ata distribuídos em 3 linhas.
// includeGk=false pula o goleiro (útil pra futsal onde o "gol" é linha).
function buildCustomSlots(schemeStr, includeGk = true) {
  const lines = String(schemeStr || '').split('-').map(n => parseInt(n, 10)).filter(n => n > 0);
  if (lines.length === 0) return includeGk ? [gk(50, 6)] : [];
  const roles = ['zag', 'meia', 'meia', 'ata']; // fallback pra role por linha
  const yStart = includeGk ? 22 : 15;
  const yEnd = 82;
  const step = lines.length > 1 ? (yEnd - yStart) / (lines.length - 1) : 0;
  const slots = includeGk ? [gk(50, 6)] : [];
  lines.forEach((n, i) => {
    const y = lines.length === 1 ? (yStart + yEnd) / 2 : yStart + i * step;
    const role = i === 0 ? 'zag' : i === lines.length - 1 ? 'ata' : (roles[i] || 'meia');
    for (let k = 0; k < n; k++) {
      const x = n === 1 ? 50 : 15 + ((85 - 15) * k) / (n - 1);
      slots.push([role, x, y]);
    }
  });
  return slots;
}

// Mapeamento role -> position_pref do cadastro pra auto-fill.
const ROLE_TO_PREFS = {
  gol:   ['goleiro'],
  zag:   ['zagueiro', 'lateral'],
  fixo:  ['zagueiro', 'volante'],
  ala:   ['lateral', 'meia'],
  meia:  ['meia', 'volante'],
  pivo:  ['atacante'],
  ata:   ['atacante', 'meia'],
};

// mode: 'community' (default, roster de community_teams/roster, owner=community_team)
//     | 'admin'     (roster de players, owner=customer). Reusa TODA a UI/CRUD.
export default function TeamLineup({ mode = 'community' }) {
  const isAdmin = mode === 'admin';
  const teamId = getActiveTeamId(); // usado apenas no mode community
  // Prioriza active_customer_id (switcher multi-time); fallback pro
  // customer_id do usuario logado quando ele nunca trocou de time.
  const activeCustomerId = isAdmin
    ? (parseInt(localStorage.getItem('active_customer_id'), 10)
        || (() => { try { return JSON.parse(localStorage.getItem('user') || '{}')?.customer_id || null; } catch { return null; } })())
    : null;
  const fieldRef = useRef(null);   // div do campo em si (click/drag/canvas)
  const captureRef = useRef(null); // wrapper pra virar imagem (inclui chip do time)
  const location = useLocation();

  // Modo sorteio: quando chega de /organizer-games via "Escalar em campo",
  // location.state carrega { sortedTeams, gameDate, gameId }. Cada time
  // vira uma "aba" — user alterna e cada um mantem seu proprio estado
  // (assignments/overrides/strokes/formacao) preservado em memoria.
  const [sortedTeams] = useState(() => location.state?.sortedTeams || null);
  const [sortedGameDate] = useState(() => location.state?.gameDate || null);
  const [activeSortedIdx, setActiveSortedIdx] = useState(0);
  const perTeamState = useRef({}); // idx -> { assignments, overrides, strokes, texts, gameType, scheme }
  const photoMapRef = useRef({});  // players.id -> profile_photo (do community_players linkado)

  const [teamName, setTeamName] = useState('');
  const [roster, setRoster] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [gameType, setGameType] = useState('society');
  const [scheme, setScheme] = useState('2-3-1');
  const [assignments, setAssignments] = useState({});   // slotIndex -> rosterItem
  const [swapModal, setSwapModal] = useState({ open: false, slotIdx: null });
  // Modo prancheta — quando ligado, jogadores viram draggable e ganham
  // botao pra trocar cor individual. Overrides sobrescrevem os defaults
  // da formacao ({x,y} em %, {color} objeto ROLE_STYLE-like).
  const [editMode, setEditMode] = useState(true);
  const [overrides, setOverrides] = useState({}); // slotIdx -> { x?, y?, color? }
  const dragRef = useRef(null); // { idx, startX, startY, origX, origY, rect }
  // Modo caneta — desenhos livres sobre o campo (funciona touch + mouse via
  // pointer events). strokes = [{ color, width, points: [{x,y in %}] }].
  const [penMode, setPenMode] = useState(false);
  const [penColor, setPenColor] = useState('#facc15');
  const [penWidth, setPenWidth] = useState(3);
  const [strokes, setStrokes] = useState([]);
  const canvasRef = useRef(null);
  const drawingRef = useRef(null); // stroke em construcao
  // Modo texto — adiciona anotacoes clicaveis/editaveis/dragga​veis sobre
  // o campo. Melhor que rabiscar com caneta pra nomes e taticas.
  // texts = [{ id, text, x, y, color, size }]
  const [textMode, setTextMode] = useState(false);
  const [textColor, setTextColor] = useState('#facc15');
  const [textSize, setTextSize] = useState(16);
  const [texts, setTexts] = useState([]);
  const [focusTextId, setFocusTextId] = useState(null); // qual texto focar apos criar
  const textDragRef = useRef(null);
  // CRUD de escalacoes salvas — owner_type=community_team, owner_id=teamId
  // do time comunidade (vem via /team-owner/my quando existir). Se o user
  // eh dono de time Futezap Pro, o mesmo flow salva com owner=customer
  // pela outra tela (TeamLineupFutezap).
  const [savedList, setSavedList] = useState([]);
  const [currentSavedId, setCurrentSavedId] = useState(null);
  const [saveModal, setSaveModal] = useState({ open: false, name: '' });
  const [openModal, setOpenModal] = useState(false);
  const [ownerTeamId, setOwnerTeamId] = useState(null); // community_teams.id do dono

  const config = FORMATIONS[gameType];
  // customSlots (null = usa o esquema padrão do FORMATIONS; array = user
  // adicionou/removeu slots via botões + / − ou está no tipo 'custom').
  // Quando muda gameType/scheme, reseta pra null e volta pro padrão.
  const [customSlots, setCustomSlots] = useState(null);
  // Params do tipo Personalizado (aparecem só quando gameType='custom').
  const [customCount, setCustomCount] = useState(9);
  const [customScheme, setCustomScheme] = useState('3-3-2');
  const slots = customSlots || config.schemes[scheme] || [];

  // Adiciona um slot novo em posição central-livre. User arrasta pra onde
  // quiser. Se algum slot arrastado ocupar coords parecidas, sistema não
  // impede — só empilha.
  const addSlot = () => {
    setCustomSlots(prev => {
      const base = prev || [...slots];
      // role default = meia (fica bem no meio). x/y = centro do campo.
      return [...base, ['meia', 50, 50]];
    });
  };
  // Remove o ÚLTIMO slot (com confirmação se tiver jogador atribuído).
  // Também limpa assignment/override do índice removido.
  const removeSlot = () => {
    const idx = slots.length - 1;
    if (idx < 1) return message.warning('Precisa ter pelo menos 1 jogador em campo');
    const attached = assignments[idx];
    const doRemove = () => {
      setCustomSlots(prev => {
        const base = prev || [...slots];
        return base.slice(0, -1);
      });
      setAssignments(prev => { const c = { ...prev }; delete c[idx]; return c; });
      setOverrides(prev => { const c = { ...prev }; delete c[idx]; return c; });
    };
    if (attached) {
      Modal.confirm({
        title: 'Remover último jogador em campo?',
        content: `${attached.name} está nessa posição. Remover mesmo assim?`,
        okText: 'Remover',
        okButtonProps: { danger: true },
        onOk: doRemove,
      });
    } else {
      doRemove();
    }
  };

  // Quando tipo custom + params mudam, regenera slots automaticamente
  // (sobrescreve customSlots com o esquema gerado).
  useEffect(() => {
    if (gameType !== 'custom') return;
    setCustomSlots(buildCustomSlots(customScheme, true));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameType, customCount, customScheme]);

  // Quando muda gameType (exceto pra custom) ou scheme, reseta customSlots
  // pra voltar ao padrão do FORMATIONS.
  useEffect(() => {
    if (gameType === 'custom') return;
    setCustomSlots(null);
  }, [gameType, scheme]);

  const load = useCallback(async () => {
    try {
      // Modo sorteio no admin: ainda faz o fetch pra popular photoMapRef
      // (usado pra colar avatar nos players do sorteio). Roster do sorteio
      // e trocado via effect quando muda activeSortedIdx.
      if (sortedTeams && !isAdmin) {
        setTeamName(localStorage.getItem('active_customer_name') || 'Meu Time');
        return;
      }
      if (isAdmin) {
        // Admin Futezap: puxa players do customer ativo. include_photos=1
        // enriquece com profile_photo do community_players linkado por
        // telefone (quem cadastrou pelo Google tem foto).
        const r = await api.get('/organizer-players', { params: { limit: 1000, page: 1, include_photos: 1 } });
        const items = (r.data.data || []).map(p => ({
          cp_id: p.id,             // reusamos slot cp_id como chave, mas eh players.id
          name: p.name,
          position_pref: String(p.position) === '1' ? 'goleiro' : 'linha',
          technical_level: p.technical_level || 5,
          profile_photo: p.profile_photo || null,
        }));
        // Modo sorteio ja setou roster com players do time ativo — guarda
        // mapa id->photo pra enriquecer os proximos sortedTeams
        photoMapRef.current = Object.fromEntries(items.map(i => [i.cp_id, i.profile_photo]));
        if (!sortedTeams) {
          setRoster(items);
        } else {
          // Modo sorteio: ja tem roster do time ativo sem fotos — mescla agora
          setRoster(prev => prev.map(r => ({ ...r, profile_photo: photoMapRef.current[r.cp_id] || null })));
        }
        setTeamName(localStorage.getItem('active_customer_name') || 'Meu Time');
      } else {
        const r = await api.get(`/community/teams/${teamId}/roster`);
        setRoster(r.data.items || []);
      }
    } catch (e) {
      message.error('Erro ao carregar elenco');
    } finally { setLoading(false); }
  }, [teamId, isAdmin, sortedTeams]);

  useEffect(() => { load(); }, [load]);

  // Modo sorteio: quando muda o time ativo, salva estado do anterior,
  // popula roster com jogadores do novo time (mapeados pro formato do
  // componente), deduz gameType pelo tamanho e restaura estado se ja
  // tinha sido editado.
  const prevSortedIdxRef = useRef(null);
  useEffect(() => {
    if (!sortedTeams) return;
    // 1) snapshot do time anterior (se ja tinha)
    const prev = prevSortedIdxRef.current;
    if (prev !== null && prev !== activeSortedIdx) {
      perTeamState.current[prev] = { assignments, overrides, strokes, texts, gameType, scheme };
    }
    // 2) monta roster do novo time. Foto vem do photoMapRef (populado
    //    quando o load do admin fez include_photos=1).
    const t = sortedTeams[activeSortedIdx];
    if (!t) return;
    const items = (t.players || []).map(p => ({
      cp_id: p.id,
      name: p.name,
      position_pref: Number(p.type) === 2 ? 'goleiro' : 'linha',
      technical_level: p.technical_level || 5,
      profile_photo: photoMapRef.current?.[p.id] || null,
    }));
    setRoster(items);
    // 3) restaura estado ou aplica defaults deduzidos pelo tamanho
    const saved = perTeamState.current[activeSortedIdx];
    if (saved) {
      setGameType(saved.gameType);
      setScheme(saved.scheme);
      setTimeout(() => {
        setOverrides(saved.overrides || {});
        setStrokes(saved.strokes || []);
        setTexts(saved.texts || []);
        setAssignments(saved.assignments || {});
      }, 30);
    } else {
      const n = items.length;
      const gt = n <= 5 ? 'salao' : n <= 8 ? 'society' : 'campo';
      const firstScheme = Object.keys(FORMATIONS[gt].schemes)[0];
      setGameType(gt);
      setScheme(firstScheme);
      setOverrides({});
      setStrokes([]);
      setTexts([]);
      // assignments monta via auto-fill quando roster/scheme mudam (effect existente)
    }
    setCurrentSavedId(null);
    prevSortedIdxRef.current = activeSortedIdx;
    // eslint-disable-next-line
  }, [sortedTeams, activeSortedIdx]);

  // Descobre o owner das lineups. Admin: customer_id direto. Comunidade:
  // busca community_team do dono logado.
  useEffect(() => {
    if (isAdmin) { setOwnerTeamId(activeCustomerId); return; }
    api.get('/community/team-owner/my')
      .then(r => {
        const t = (r.data.teams || []).find(x => x.role === 'owner');
        if (t) setOwnerTeamId(t.id);
      })
      .catch(() => {});
  }, [isAdmin, activeCustomerId]);

  const ownerType = isAdmin ? 'customer' : 'community_team';

  // Track de uso — feito no Overview do admin pra medir adocao da feature.
  // Best-effort, silencioso, nao interfere no fluxo.
  const track = useCallback((event, meta) => {
    if (!ownerTeamId) return;
    api.post('/lineups/track', { event, owner_type: ownerType, owner_id: ownerTeamId, meta })
      .catch(() => {/* silencioso */});
  }, [ownerType, ownerTeamId]);

  const loadSavedList = useCallback(async () => {
    if (!ownerTeamId) return;
    try {
      const r = await api.get('/lineups', { params: { owner_type: ownerType, owner_id: ownerTeamId } });
      setSavedList(r.data.items || []);
    } catch {/* silencioso */}
  }, [ownerTeamId, ownerType]);
  useEffect(() => { loadSavedList(); }, [loadSavedList]);

  // View: 1x quando ownerTeamId fica disponivel (nao roda no reload de
  // trocas de time do sorteio pra nao inflar).
  useEffect(() => { if (ownerTeamId) track('view', { mode }); }, [ownerTeamId, mode, track]);

  // Muda formacao/tipo -> descarta overrides (posicoes/cores customizadas)
  useEffect(() => { setOverrides({}); setStrokes([]); setTexts([]); }, [gameType, scheme]);

  // Ao mudar formação, tenta auto-fill respeitando position_pref. Pula
  // quando o time ativo do sorteio ja tem estado salvo restaurado.
  const rosterKey = useMemo(() => roster.map(r => r.cp_id).join(','), [roster]);
  useEffect(() => {
    if (!roster.length) return;
    // Modo sorteio: se acabou de restaurar estado do time (perTeamState
    // ja tem entry pra esse idx), nao sobrescreve.
    if (sortedTeams && perTeamState.current[activeSortedIdx]?.assignments) return;
    const usedIds = new Set();
    const newAssign = {};
    // Primeira passada: match por position_pref exato
    slots.forEach((slot, idx) => {
      const [role] = slot;
      const preferences = ROLE_TO_PREFS[role] || [];
      const cand = roster.find(r => !usedIds.has(r.cp_id) && preferences.includes(r.position_pref));
      if (cand) { newAssign[idx] = cand; usedIds.add(cand.cp_id); }
    });
    // Segunda passada: preenche slots vazios com quem sobrou
    slots.forEach((_, idx) => {
      if (newAssign[idx]) return;
      const cand = roster.find(r => !usedIds.has(r.cp_id));
      if (cand) { newAssign[idx] = cand; usedIds.add(cand.cp_id); }
    });
    setAssignments(newAssign);
    // eslint-disable-next-line
  }, [gameType, scheme, rosterKey]);

  const download = async () => {
    if (!captureRef.current) return;
    setSaving(true);
    try {
      const png = await htmlToImage.toPng(captureRef.current, {
        pixelRatio: 2, backgroundColor: '#0b1f1a',
      });
      const a = document.createElement('a');
      a.download = `escalacao-${teamName.replace(/\s+/g, '-').toLowerCase()}-${scheme}.png`;
      a.href = png; a.click();
      track('download', { game_type: gameType, scheme });
    } catch (e) {
      message.error('Falhou gerar imagem');
    } finally { setSaving(false); }
  };

  const [broadcasting, setBroadcasting] = useState(false);
  // Modo tela cheia — campo ocupa 100vw x 100vh pra explicar tatica no
  // vestiario/beira do campo. Zoom o suficiente pra bolhas serem clicaveis
  // no celular na mao.
  const [fullscreen, setFullscreen] = useState(false);
  useEffect(() => {
    if (!fullscreen) return;
    const onKey = (e) => { if (e.key === 'Escape') setFullscreen(false); };
    window.addEventListener('keydown', onKey);
    document.body.style.overflow = 'hidden';
    return () => {
      window.removeEventListener('keydown', onKey);
      document.body.style.overflow = '';
    };
  }, [fullscreen]);
  // Envia PNG de cada time sorteado pro grupo do time. Fluxo:
  // 1) check cooldown backend
  // 2) snapshot estado do time atualmente ativo em perTeamState
  // 3) itera trocando activeSortedIdx, gera PNG (aguarda re-render)
  // 4) POST array pro backend -> Evolution sequencial
  const broadcastToGroup = async () => {
    if (!sortedTeams || !sortedTeams.length) return;
    // check cooldown antes de gerar imagem (evita esforco desperdicado)
    try {
      const cd = await api.get('/lineups/broadcast-cooldown');
      if (cd.data.available === false) {
        return Modal.warning({
          title: 'Aguarda um pouco',
          content: `Você mandou uma escalação faz pouco. Pode enviar de novo em ${cd.data.minutes_left} min. Isso evita spam no grupo enquanto você testa vários sorteios.`,
        });
      }
    } catch { /* silencioso — segue e o backend re-valida */ }

    Modal.confirm({
      title: 'Enviar escalações no grupo?',
      content: `Vou gerar uma imagem de cada time (${sortedTeams.length}) e mandar pro grupo do WhatsApp. Depois disso, novo envio só em 30 min.`,
      okText: 'Enviar', cancelText: 'Cancelar',
      onOk: async () => {
        setBroadcasting(true);
        // Salva estado do time atual antes de iterar
        perTeamState.current[activeSortedIdx] = { assignments, overrides, strokes, texts, gameType, scheme };
        const startIdx = activeSortedIdx;
        const images = [];
        try {
          for (let i = 0; i < sortedTeams.length; i++) {
            setActiveSortedIdx(i);
            // Aguarda o effect trocar roster + restaurar estado + re-render
            await new Promise(r => setTimeout(r, 600));
            if (!captureRef.current) continue;
            const png = await htmlToImage.toPng(captureRef.current, {
              pixelRatio: 2, backgroundColor: '#0b1f1a',
            });
            images.push({ name: sortedTeams[i].name, png });
          }
          // volta pro time original
          setActiveSortedIdx(startIdx);

          const r = await api.post('/lineups/broadcast-to-group', {
            images,
            caption: `Escalação ${sortedGameDate ? '· ' + String(sortedGameDate).slice(0, 10) : ''}`,
          });
          if (r.data.blocked) {
            message.warning(r.data.message || 'Bloqueado por cooldown');
          } else if (r.data.sent === r.data.total) {
            track('broadcast', { count: r.data.total, source: 'sorteio' });
            message.success(`Mandei ${r.data.sent} imagens no grupo. Próximo envio só em 30 min.`);
          } else {
            track('broadcast', { count: r.data.sent, total: r.data.total, source: 'sorteio', partial: true });
            message.warning(`Enviei ${r.data.sent} de ${r.data.total}. Alguma imagem falhou.`);
          }
        } catch (e) {
          if (e.response?.status === 429) {
            const min = e.response.data?.minutes_left;
            message.warning(`Cooldown ativo — tenta de novo em ${min || 30} min.`);
          } else {
            message.error(e.response?.data?.message || 'Falhou mandar pro grupo');
          }
        } finally {
          setBroadcasting(false);
        }
      },
    });
  };

  const doSwap = (slotIdx, replacement) => {
    const current = assignments[slotIdx];
    // Se o novo já está em outro slot, faz troca
    const otherSlot = Object.entries(assignments).find(([_, v]) => v && v.cp_id === replacement?.cp_id)?.[0];
    const next = { ...assignments };
    next[slotIdx] = replacement;
    if (otherSlot !== undefined && current) next[otherSlot] = current;
    else if (otherSlot !== undefined) delete next[otherSlot];
    setAssignments(next);
    setSwapModal({ open: false, slotIdx: null });
  };

  // Canvas de desenho — redimensiona pra CSS pixels do container, redesenha
  // strokes normalizados (0-100%) sempre que muda.
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;
    const rect = canvas.getBoundingClientRect();
    const dpr = window.devicePixelRatio || 1;
    canvas.width = rect.width * dpr;
    canvas.height = rect.height * dpr;
    const ctx = canvas.getContext('2d');
    ctx.setTransform(dpr, 0, 0, dpr, 0, 0);
    ctx.clearRect(0, 0, rect.width, rect.height);
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    for (const st of strokes) {
      if (!st.points.length) continue;
      ctx.strokeStyle = st.color;
      ctx.lineWidth = st.width;
      ctx.beginPath();
      st.points.forEach((p, i) => {
        const px = (p.x / 100) * rect.width;
        const py = (p.y / 100) * rect.height;
        if (i === 0) ctx.moveTo(px, py); else ctx.lineTo(px, py);
      });
      ctx.stroke();
    }
  }, [strokes, penMode, gameType, scheme]);

  const canvasPointerDown = (e) => {
    if (!penMode) return;
    e.preventDefault();
    const rect = canvasRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    drawingRef.current = { color: penColor, width: penWidth, points: [{ x, y }] };
    setStrokes(prev => [...prev, drawingRef.current]);
    const onMove = (ev) => {
      const d = drawingRef.current;
      if (!d) return;
      const r = canvasRef.current.getBoundingClientRect();
      const nx = ((ev.clientX - r.left) / r.width) * 100;
      const ny = ((ev.clientY - r.top) / r.height) * 100;
      d.points.push({ x: nx, y: ny });
      setStrokes(prev => [...prev.slice(0, -1), { ...d, points: [...d.points] }]);
    };
    const onUp = () => {
      drawingRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const undoStroke = () => setStrokes(prev => prev.slice(0, -1));
  const clearStrokes = () => setStrokes([]);

  // Modo texto — clique no fundo do campo cria caixa nova naquela posicao.
  // Se clicou em outro elemento (bubble/texto existente), nao cria.
  const handleFieldClick = (e) => {
    if (!textMode) return;
    if (e.target !== e.currentTarget) return; // click veio de child (bubble/texto)
    const rect = fieldRef.current.getBoundingClientRect();
    const x = ((e.clientX - rect.left) / rect.width) * 100;
    const y = ((e.clientY - rect.top) / rect.height) * 100;
    const id = Date.now();
    setTexts(prev => [...prev, { id, text: '', x, y, color: textColor, size: textSize }]);
    setFocusTextId(id);
  };

  const startDragText = (e, id, curPos) => {
    if (!editMode && !textMode) return;
    e.preventDefault();
    const rect = fieldRef.current.getBoundingClientRect();
    textDragRef.current = {
      id, startX: e.clientX, startY: e.clientY,
      origX: curPos.x, origY: curPos.y, rect, moved: false,
    };
    const onMove = (ev) => {
      const d = textDragRef.current;
      if (!d) return;
      const dxPct = ((ev.clientX - d.startX) / d.rect.width) * 100;
      const dyPct = ((ev.clientY - d.startY) / d.rect.height) * 100;
      if (Math.abs(dxPct) > 0.5 || Math.abs(dyPct) > 0.5) d.moved = true;
      const nx = Math.max(2, Math.min(98, d.origX + dxPct));
      const ny = Math.max(2, Math.min(98, d.origY + dyPct));
      setTexts(prev => prev.map(t => t.id === d.id ? { ...t, x: nx, y: ny } : t));
    };
    const onUp = () => {
      textDragRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  // Serializa estado completo pra salvar. Assignments guarda so cp_id — na
  // carga o front resolve pra objeto full a partir do roster atual.
  const serialize = () => ({
    gameType, scheme,
    // customSlots persistido pra restaurar quando load. Se null, load usa
    // padrão do FORMATIONS[gameType].schemes[scheme].
    customSlots,
    customCount,
    customScheme,
    overrides,
    strokes,
    texts,
    assignments: Object.fromEntries(
      Object.entries(assignments).map(([idx, p]) => [idx, p?.cp_id]).filter(([, v]) => v)
    ),
  });

  const applyLoaded = (row) => {
    const d = row.data || {};
    setGameType(row.game_type);
    setScheme(row.scheme);
    // Ordem: seta gameType/scheme primeiro, dai o effect que zera overrides
    // dispara. Aplica overrides/strokes/assignments/customSlots em microtask
    // depois (senão o effect de reset apagaria o customSlots recém carregado).
    setTimeout(() => {
      setOverrides(d.overrides || {});
      setStrokes(d.strokes || []);
      setTexts(d.texts || []);
      // customSlots antigos (antes desta feature) não têm essas keys — usam
      // o padrão do FORMATIONS. Só aplica se veio no serialize novo.
      if (d.customSlots !== undefined) setCustomSlots(d.customSlots);
      if (d.customCount) setCustomCount(d.customCount);
      if (d.customScheme) setCustomScheme(d.customScheme);
      const newAssign = {};
      Object.entries(d.assignments || {}).forEach(([idx, cpId]) => {
        const p = roster.find(r => r.cp_id === cpId);
        if (p) newAssign[idx] = p;
      });
      setAssignments(newAssign);
      setCurrentSavedId(row.id);
    }, 30);
    setOpenModal(false);
    message.success(`Escalação "${row.name}" carregada`);
  };

  const saveNew = async () => {
    const name = saveModal.name.trim();
    if (!name) return message.warning('Dá um nome');
    if (!ownerTeamId) return message.error('Time nao encontrado');
    try {
      const r = await api.post('/lineups', {
        owner_type: ownerType, owner_id: ownerTeamId,
        name, game_type: gameType, scheme, data: serialize(),
      });
      setCurrentSavedId(r.data.id);
      setSaveModal({ open: false, name: '' });
      loadSavedList();
      track('save', { new: true, game_type: gameType, scheme });
      message.success('Escalação salva');
    } catch (e) { message.error(e.response?.data?.message || 'Falhou salvar'); }
  };

  const overwriteCurrent = async () => {
    if (!currentSavedId) return;
    try {
      await api.put(`/lineups/${currentSavedId}`, {
        game_type: gameType, scheme, data: serialize(),
      });
      loadSavedList();
      track('save', { new: false, game_type: gameType, scheme });
      message.success('Escalação atualizada');
    } catch (e) { message.error(e.response?.data?.message || 'Falhou salvar'); }
  };

  const openSaved = async (id) => {
    try {
      const r = await api.get(`/lineups/${id}`);
      applyLoaded(r.data);
      track('open', { lineup_id: id });
    } catch (e) { message.error('Falhou carregar'); }
  };

  const deleteSaved = async (id) => {
    Modal.confirm({
      title: 'Excluir escalação?',
      okText: 'Excluir', okType: 'danger', cancelText: 'Cancelar',
      onOk: async () => {
        try {
          await api.delete(`/lineups/${id}`);
          if (currentSavedId === id) setCurrentSavedId(null);
          loadSavedList();
        } catch { message.error('Falhou excluir'); }
      }
    });
  };

  // Drag livre no campo. Usa PointerEvent (mouse + touch) e converte
  // deltaPX pra % do container do campo pra continuar responsivo.
  const startDrag = (e, idx, currentTopLeft) => {
    if (!editMode) return;
    e.preventDefault();
    const rect = fieldRef.current.getBoundingClientRect();
    dragRef.current = {
      idx,
      startX: e.clientX, startY: e.clientY,
      origLeft: currentTopLeft.left, origTop: currentTopLeft.top,
      rect,
    };
    const onMove = (ev) => {
      const d = dragRef.current;
      if (!d) return;
      const dxPct = ((ev.clientX - d.startX) / d.rect.width) * 100;
      const dyPct = ((ev.clientY - d.startY) / d.rect.height) * 100;
      let nx = Math.max(2, Math.min(98, d.origLeft + dxPct));
      let ny = Math.max(2, Math.min(98, d.origTop + dyPct));
      setOverrides(prev => ({ ...prev, [d.idx]: { ...prev[d.idx], x: nx, y: ny } }));
    };
    const onUp = () => {
      dragRef.current = null;
      window.removeEventListener('pointermove', onMove);
      window.removeEventListener('pointerup', onUp);
    };
    window.addEventListener('pointermove', onMove);
    window.addEventListener('pointerup', onUp);
  };

  const available = useMemo(() => {
    const usedIds = new Set(Object.values(assignments).filter(Boolean).map(x => x.cp_id));
    return roster.filter(r => !usedIds.has(r.cp_id));
  }, [assignments, roster]);

  if (loading) return <div style={{ textAlign: 'center', padding: 80 }}><Spin size="large" /></div>;

  return (
    <div style={{ padding: 16, maxWidth: 1200, margin: '0 auto' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 12 }}>
        <Button type="text" icon={<LeftOutlined />} onClick={() => window.history.back()} />
        <Title level={3} style={{ margin: 0 }}>Escalação em campo</Title>
      </div>
      <Text type="secondary" style={{ fontSize: 12, display: 'block', marginBottom: 16 }}>
        {editMode
          ? 'Modo prancheta: arrasta os jogadores no campo, clica no ícone da paleta pra trocar cor, ou usa o botão de troca pra mudar quem tá no slot. Depois baixa como imagem.'
          : 'Visualização final. Toque no cadeado pra editar de novo.'}
      </Text>

      {sortedTeams && sortedTeams.length > 0 && (
        <Card size="small" style={{ marginBottom: 12, background: '#f0fdf4', borderColor: '#25D366' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
            <Tag color="green" style={{ margin: 0 }}>Sorteio {sortedGameDate || ''}</Tag>
            <Text style={{ fontSize: 12, color: '#5a6a63' }}>Escolhe o time pra escalar:</Text>
            <Segmented
              value={activeSortedIdx}
              onChange={setActiveSortedIdx}
              options={sortedTeams.map((t, i) => ({
                label: `${t.name} (${t.players?.length || 0})`,
                value: i,
              }))}
            />
            <div style={{ flex: 1 }} />
            <Button type="primary" icon={<WhatsAppOutlined />}
              loading={broadcasting} onClick={broadcastToGroup}>
              Enviar todos pro grupo
            </Button>
          </div>
        </Card>
      )}

      <Card size="small" style={{ marginBottom: 12 }}>
        <Row gutter={[12, 12]} align="middle">
          <Col xs={24} md={gameType === 'custom' ? 8 : 12}>
            <div style={{ marginBottom: 4, fontSize: 12, color: '#8c8c8c' }}>Tipo</div>
            <Select
              value={gameType} onChange={v => { setGameType(v); setScheme(Object.keys(FORMATIONS[v].schemes)[0]); }}
              style={{ width: '100%' }}
              options={Object.entries(FORMATIONS).map(([k, v]) => ({ value: k, label: v.label }))}
            />
          </Col>
          {gameType === 'custom' ? (
            <>
              <Col xs={12} md={8}>
                <div style={{ marginBottom: 4, fontSize: 12, color: '#8c8c8c' }}>Em campo</div>
                <Select
                  value={customCount} onChange={setCustomCount}
                  style={{ width: '100%' }}
                  options={Array.from({ length: 14 }, (_, i) => i + 2).map(n => ({ value: n, label: `${n} jogadores` }))}
                />
              </Col>
              <Col xs={12} md={8}>
                <Tooltip title="Ex: 3-3-2 = 3 zag, 3 meia, 2 ata (goleiro à parte). Total precisa bater com 'Em campo' menos 1 (goleiro).">
                  <div style={{ marginBottom: 4, fontSize: 12, color: '#8c8c8c' }}>
                    Esquema <InfoCircleOutlined style={{ color: '#bfbfbf', fontSize: 11 }} />
                  </div>
                </Tooltip>
                <Input
                  value={customScheme}
                  onChange={e => setCustomScheme(e.target.value.replace(/[^0-9-]/g, ''))}
                  placeholder="Ex: 3-3-2"
                  maxLength={15}
                />
              </Col>
            </>
          ) : (
            <Col xs={24} md={12}>
              <div style={{ marginBottom: 4, fontSize: 12, color: '#8c8c8c' }}>Formação</div>
              <Select
                value={scheme} onChange={setScheme}
                style={{ width: '100%' }}
                options={Object.keys(config.schemes).map(s => ({ value: s, label: s }))}
              />
            </Col>
          )}
          <Col span={24}>
            <Space size={8} wrap>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Ajustar jogadores em campo: <strong>{slots.length}</strong>
              </Text>
              <Button size="small" icon={<PlusOutlined />} onClick={addSlot}>Adicionar</Button>
              <Button size="small" icon={<MinusOutlined />} onClick={removeSlot} disabled={slots.length <= 1}>
                Remover
              </Button>
              {customSlots && gameType !== 'custom' && (
                <Button size="small" type="link" onClick={() => setCustomSlots(null)} style={{ padding: 0 }}>
                  Voltar ao padrão da formação
                </Button>
              )}
            </Space>
          </Col>
          <Col span={24}>
            <Space wrap size={[8, 8]}>
              {/* Modos: edit / caneta / texto — com label pra ficar claro
                  qual ta ativo. Ações auxiliares (reset/save/open/fullscreen/
                  baixar) viram so icone pra caber melhor em mobile. */}
              <Tooltip title={editMode ? 'Modo prancheta ligado — desativa pra baixar imagem limpa' : 'Ativar edição'}>
                <Button icon={editMode ? <EditOutlined /> : <DragOutlined />}
                  type={editMode ? 'primary' : 'default'}
                  onClick={() => { setEditMode(v => !v); if (penMode) setPenMode(false); }}>
                  {editMode ? 'Editando' : 'Editar'}
                </Button>
              </Tooltip>
              <Tooltip title={penMode ? 'Desativa a caneta' : 'Rabiscar sobre o campo'}>
                <Button icon={<HighlightOutlined />}
                  type={penMode ? 'primary' : 'default'}
                  onClick={() => { setPenMode(v => !v); if (textMode) setTextMode(false); }}>
                  {penMode ? 'Caneta' : 'Rabiscar'}
                </Button>
              </Tooltip>
              <Tooltip title={textMode ? 'Desativa modo texto' : 'Clique no campo pra adicionar texto'}>
                <Button icon={<FontSizeOutlined />}
                  type={textMode ? 'primary' : 'default'}
                  onClick={() => { setTextMode(v => !v); if (penMode) setPenMode(false); }}>
                  Texto
                </Button>
              </Tooltip>
              <Tooltip title="Resetar posições, cores, rabiscos e textos">
                <Button icon={<ReloadOutlined />} onClick={() => { setOverrides({}); setStrokes([]); setTexts([]); setCurrentSavedId(null); }} />
              </Tooltip>
              <Tooltip title={currentSavedId ? 'Atualizar escalação salva' : 'Salvar escalação'}>
                <Button icon={<SaveOutlined />}
                  onClick={() => {
                    if (currentSavedId) return overwriteCurrent();
                    let suggested = '';
                    if (sortedTeams && sortedTeams[activeSortedIdx]) {
                      const t = sortedTeams[activeSortedIdx];
                      const d = sortedGameDate ? String(sortedGameDate).slice(0, 10) : '';
                      suggested = `Sorteio${d ? ' ' + d : ''} · ${t.name}`;
                    }
                    setSaveModal({ open: true, name: suggested });
                  }} />
              </Tooltip>
              <Tooltip title="Abrir escalação salva">
                <Button icon={<FolderOpenOutlined />} onClick={() => setOpenModal(true)} />
              </Tooltip>
              <Tooltip title="Modo tela cheia (bom pra explicar no vestiário)">
                <Button icon={<ExpandOutlined />} onClick={() => setFullscreen(true)}>
                  Tela cheia
                </Button>
              </Tooltip>
              <Tooltip title="Baixar como imagem PNG">
                <Button icon={<DownloadOutlined />} onClick={download} loading={saving} />
              </Tooltip>
            </Space>
          </Col>
        </Row>
      </Card>

      {penMode && (
        <Card size="small" style={{ marginBottom: 8 }}>
          <Space wrap>
            <span style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 600 }}>Caneta:</span>
            <ColorPicker value={penColor} onChange={c => setPenColor(c?.toHexString?.() || c)}
              disabledAlpha
              presets={[{ label: 'Traços', colors: ['#facc15', '#ef4444', '#25D366', '#3b82f6', '#ffffff', '#111827', '#f97316', '#a855f7'] }]}
            >
              <Button size="small" style={{ background: penColor, borderColor: penColor, color: contrastText(penColor) }}>
                Cor
              </Button>
            </ColorPicker>
            <Space size={4}>
              {[2, 3, 5, 8].map(w => (
                <Button key={w} size="small"
                  type={penWidth === w ? 'primary' : 'default'}
                  onClick={() => setPenWidth(w)}>
                  {w}px
                </Button>
              ))}
            </Space>
            <Button size="small" icon={<UndoOutlined />} onClick={undoStroke} disabled={!strokes.length}>
              Desfazer
            </Button>
            <Button size="small" danger icon={<DeleteOutlined />} onClick={clearStrokes} disabled={!strokes.length}>
              Limpar rabiscos
            </Button>
          </Space>
        </Card>
      )}

      {textMode && (
        <Card size="small" style={{ marginBottom: 8 }}>
          <Space wrap>
            <span style={{ fontSize: 12, color: '#8c8c8c', fontWeight: 600 }}>Texto: clique no campo pra adicionar.</span>
            <ColorPicker value={textColor} onChange={c => setTextColor(c?.toHexString?.() || c)}
              disabledAlpha
              presets={[{ label: 'Cores', colors: ['#facc15', '#ffffff', '#ef4444', '#25D366', '#3b82f6', '#111827', '#f97316', '#a855f7'] }]}
            >
              <Button size="small" style={{ background: textColor, borderColor: textColor, color: contrastText(textColor) }}>
                Cor
              </Button>
            </ColorPicker>
            <Space size={4}>
              {[12, 16, 20, 26].map(s => (
                <Button key={s} size="small"
                  type={textSize === s ? 'primary' : 'default'}
                  onClick={() => setTextSize(s)}>
                  {s}px
                </Button>
              ))}
            </Space>
            <Button size="small" danger icon={<DeleteOutlined />} onClick={() => setTexts([])} disabled={!texts.length}>
              Limpar textos
            </Button>
          </Space>
        </Card>
      )}

      <Row gutter={12}>
        <Col xs={24} md={16}>
          <div ref={captureRef}
            style={fullscreen ? {
              position: 'fixed', inset: 0, zIndex: 10000,
              background: '#0b1f1a', padding: 16,
              display: 'flex', flexDirection: 'column',
            } : undefined}>
          {fullscreen && (
            <Button icon={<CompressOutlined />} onClick={() => setFullscreen(false)}
              style={{ position: 'fixed', top: 12, right: 12, zIndex: 10001 }}>
              Fechar
            </Button>
          )}
          {/* Chip com nome do time + esquema — fora do campo pra nao pisar
              nas linhas. Aparece na captura do PNG tambem (fica dentro do
              wrapper captureRef que o htmlToImage renderiza). */}
          <div style={{
            maxWidth: fullscreen ? 'none' : 450, margin: '0 auto 8px auto',
            display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            padding: '8px 14px', borderRadius: 12, width: fullscreen ? '100%' : undefined,
            background: 'rgba(0,0,0,0.75)', color: '#fff', fontWeight: 800,
          }}>
            <div style={{ fontSize: 14 }}>{teamName || 'MEU TIME'} · {scheme}</div>
            <div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, opacity: 0.85 }}>
              <img src="/pwa/icon-192.png" alt="" width={18} height={18} style={{ borderRadius: 4 }} />
              FUTEZAP
            </div>
          </div>
          <div ref={fieldRef}
            onClick={handleFieldClick}
            style={{
              position: 'relative', width: '100%', aspectRatio: '3/4',
              // Trava tamanho maximo do campo pra caber na tela sem scroll
              // longo. Em mobile o width:100% cai naturalmente com aspect
              // ratio. Em desktop, para em 450x600 — tamanho confortavel
              // e ainda otimo pra virar imagem no WhatsApp. Fullscreen
              // ignora esses limites e expande pra viewport inteira.
              maxWidth: fullscreen ? 'none' : 450,
              maxHeight: fullscreen ? 'none' : 600,
              flex: fullscreen ? 1 : undefined,
              margin: '0 auto',
              borderRadius: 20, overflow: 'hidden',
              background: SURFACE[gameType]?.bg || SURFACE.society.bg,
              boxShadow: '0 12px 40px rgba(0,0,0,0.35)',
              cursor: textMode ? 'text' : 'default',
            }}>
            <FieldSurface type={gameType} />
            <FieldLines type={gameType} />
            {/* Canvas de desenho — pointerEvents:auto quando penMode pra
                capturar traços; none caso contrario pra jogadores receberem
                clicks/drag normalmente. Fica entre as linhas e as bolhas. */}
            <canvas
              ref={canvasRef}
              onPointerDown={canvasPointerDown}
              style={{
                position: 'absolute', inset: 0, width: '100%', height: '100%',
                zIndex: 2, pointerEvents: penMode ? 'auto' : 'none',
                touchAction: 'none', cursor: penMode ? 'crosshair' : 'default',
              }} />
            {slots.map((slot, idx) => {
              const [role, defaultX, defaultY] = slot;
              const player = assignments[idx];
              const ov = overrides[idx] || {};
              const x = ov.x != null ? ov.x : defaultX;
              // y=0 = topo defensivo original. Como escalação intuitiva quer
              // gol embaixo, top = 100 - y (a menos que override ja seja em top%)
              const yRaw = ov.y != null ? ov.y : (100 - defaultY);
              // Comprime 0-100 pra 6-94 pra dar respiro nas bordas — o nome
              // do goleiro embaixo estava cortando na hora de renderizar.
              const yBase = 6 + yRaw * 0.88;
              // Aplica mesma margem lateral (comprime 0-100 pra 5-95)
              const xBase = 5 + x * 0.9;
              return (
                <PlayerBubble
                  key={idx} idx={idx} role={role}
                  left={xBase} top={yBase}
                  player={player}
                  editMode={editMode}
                  customColor={ov.color}
                  onSwap={() => setSwapModal({ open: true, slotIdx: idx })}
                  onColorChange={c => setOverrides(p => ({ ...p, [idx]: { ...p[idx], color: c } }))}
                  onDragStart={(e, cur) => startDrag(e, idx, cur)}
                />
              );
            })}
            {texts.map(t => (
              <TextItem key={t.id} data={t}
                autoFocus={focusTextId === t.id}
                onFocused={() => setFocusTextId(null)}
                onChange={(patch) => setTexts(prev => prev.map(x => x.id === t.id ? { ...x, ...patch } : x))}
                onDelete={() => setTexts(prev => prev.filter(x => x.id !== t.id))}
                onDragStart={(e) => startDragText(e, t.id, { x: t.x, y: t.y })}
                editable={textMode || editMode}
              />
            ))}
          </div>
          </div>
        </Col>
        <Col xs={24} md={8}>
          <Card size="small" title={<><TeamOutlined /> Reservas ({available.length})</>}
            style={{ maxHeight: 700, overflowY: 'auto' }}>
            {available.length === 0 ? (
              <Empty description="Todos escalados" />
            ) : (
              available.map(p => (
                <div key={p.cp_id} style={{
                  padding: '8px 4px', borderBottom: '1px solid #eee',
                  display: 'flex', alignItems: 'center', gap: 8, fontSize: 13,
                }}>
                  <UserOutlined style={{ color: '#8c8c8c' }} />
                  <span style={{ flex: 1, fontWeight: 500 }}>{p.name}</span>
                  {p.position_pref && <span style={{ fontSize: 11, color: '#8c8c8c' }}>{p.position_pref}</span>}
                </div>
              ))
            )}
          </Card>
        </Col>
      </Row>

      <Modal
        title="Trocar jogador"
        open={swapModal.open}
        onCancel={() => setSwapModal({ open: false, slotIdx: null })}
        footer={null}
        destroyOnClose
      >
        <div style={{ maxHeight: 400, overflowY: 'auto' }}>
          <div style={{ padding: 8, background: '#fff7ed', borderRadius: 8, marginBottom: 12 }}>
            <div style={{ fontSize: 12, color: '#c2410c', fontWeight: 700 }}>Escolha o novo jogador</div>
          </div>
          {roster.map(p => (
            <div key={p.cp_id || `fzp_${p.futezap_player_id}`}
              onClick={() => p.cp_id && doSwap(swapModal.slotIdx, p)}
              style={{
                padding: '10px 8px', borderBottom: '1px solid #eee', cursor: p.cp_id ? 'pointer' : 'not-allowed',
                display: 'flex', alignItems: 'center', gap: 10, opacity: p.cp_id ? 1 : 0.5,
              }}>
              <UserOutlined style={{ color: '#8c8c8c' }} />
              <div style={{ flex: 1 }}>
                <div style={{ fontWeight: 600 }}>{p.name}</div>
                {p.position_pref && <div style={{ fontSize: 11, color: '#8c8c8c' }}>{p.position_pref}</div>}
              </div>
            </div>
          ))}
          {swapModal.slotIdx != null && assignments[swapModal.slotIdx] && (
            <Button block danger style={{ marginTop: 12 }}
              onClick={() => { const n = { ...assignments }; delete n[swapModal.slotIdx]; setAssignments(n); setSwapModal({ open: false, slotIdx: null }); }}>
              Deixar slot vazio
            </Button>
          )}
        </div>
      </Modal>

      {/* Modal Salvar como novo */}
      <Modal title="Salvar escalação" open={saveModal.open}
        onCancel={() => setSaveModal({ open: false, name: '' })}
        onOk={saveNew} okText="Salvar" destroyOnClose>
        <Input autoFocus placeholder="Ex: Time A titular, Sem Marcos, Amistoso vs XYZ"
          value={saveModal.name}
          onChange={e => setSaveModal({ open: true, name: e.target.value })}
          onPressEnter={saveNew} maxLength={80} />
      </Modal>

      {/* Modal Abrir escalação salva */}
      <Modal title="Escalações salvas" open={openModal}
        onCancel={() => setOpenModal(false)} footer={null} destroyOnClose>
        {savedList.length === 0 ? (
          <Empty description="Nenhuma escalação salva ainda" />
        ) : (
          <div style={{ maxHeight: 420, overflowY: 'auto' }}>
            {savedList.map(l => (
              <div key={l.id} style={{
                display: 'flex', alignItems: 'center', gap: 12,
                padding: 12, borderBottom: '1px solid #eee',
                background: l.id === currentSavedId ? '#f0fdf4' : 'transparent',
              }}>
                <div style={{ flex: 1, cursor: 'pointer' }} onClick={() => openSaved(l.id)}>
                  <div style={{ fontWeight: 700 }}>{l.name}</div>
                  <div style={{ fontSize: 11, color: '#8c8c8c' }}>
                    {l.game_type} · {l.scheme} · {new Date(l.updated_at).toLocaleString('pt-BR')}
                  </div>
                </div>
                <Button size="small" onClick={() => openSaved(l.id)}>Abrir</Button>
                <Button size="small" danger icon={<DeleteOutlined />} onClick={() => deleteSaved(l.id)} />
              </div>
            ))}
          </div>
        )}
      </Modal>
    </div>
  );
}

function PlayerBubble({
  idx, role, left, top, player, editMode,
  customColor, onSwap, onColorChange, onDragStart,
}) {
  const baseSkin = ROLE_STYLE[role] || ROLE_STYLE.meia;
  const skin = customColor ? colorToSkin(customColor) : baseSkin;
  const number = player?.jersey_number || '';
  const name = player?.name?.split(' ')[0] || '—';
  const initial = player?.name ? player.name.trim()[0]?.toUpperCase() : '';
  const photo = player?.profile_photo || null;
  const empty = !player;
  // "+" so em slot vazio. Slot preenchido mostra: foto > numero > inicial.
  const centerContent = empty ? '+' : (photo ? null : (number || initial));
  // Em editMode, distingue click curto (abre swap) de drag (move). Marca
  // startX/startY, se pouco delta no pointerup => click.
  const clickTrackRef = useRef(null);
  const handleDown = (e) => {
    if (!editMode) return;
    clickTrackRef.current = { x: e.clientX, y: e.clientY, t: Date.now() };
    onDragStart(e, { left, top });
  };
  const handleUp = (e) => {
    if (!editMode) return;
    const ct = clickTrackRef.current;
    if (!ct) return;
    const dx = Math.abs(e.clientX - ct.x);
    const dy = Math.abs(e.clientY - ct.y);
    const dt = Date.now() - ct.t;
    if (dx < 5 && dy < 5 && dt < 500) onSwap?.();
    clickTrackRef.current = null;
  };
  const bubble = (
    <div
      onPointerDown={handleDown}
      onPointerUp={handleUp}
      style={{
        position: 'absolute', left: `${left}%`, top: `${top}%`, transform: 'translate(-50%, -50%)',
        display: 'flex', flexDirection: 'column', alignItems: 'center',
        cursor: editMode ? 'grab' : 'pointer', zIndex: 3,
        touchAction: 'none', // impede scroll da pagina durante drag no mobile
        userSelect: 'none',
      }}>
      <div style={{
        width: 46, height: 46, borderRadius: 23,
        background: empty ? 'transparent'
          : (photo ? `#000` : `radial-gradient(circle at 30% 25%, ${skin.hi} 0%, ${skin.mid} 55%, ${skin.lo} 100%)`),
        border: empty ? '2px dashed rgba(255,255,255,0.4)' : `2px solid ${skin.ring}`,
        color: empty ? 'rgba(255,255,255,0.55)' : '#fff', fontWeight: 900, fontSize: 15,
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        boxShadow: empty ? 'none' : `0 6px 14px rgba(0,0,0,0.45), inset 0 -3px 6px rgba(0,0,0,0.25)`,
        textShadow: empty ? 'none' : '0 1px 2px rgba(0,0,0,0.6)',
        position: 'relative', overflow: 'hidden',
      }}>
        {photo ? (
          <img src={photo} alt="" crossOrigin="anonymous"
            style={{ width: '100%', height: '100%', objectFit: 'cover', borderRadius: '50%' }}
            onError={(e) => { e.currentTarget.style.display = 'none'; }} />
        ) : centerContent}
        {editMode && (
          <>
            <ColorPicker
              value={typeof customColor === 'string' ? customColor : (customColor?.toHexString?.() || skin.mid)}
              onChange={(c) => onColorChange(c?.toHexString?.() || c)}
              trigger="click"
              disabledAlpha
              presets={[
                { label: 'Futezap', colors: ['#25D366', '#128C7E', '#f59e0b', '#dc2626', '#0891b2', '#f97316', '#8b5cf6', '#3b82f6', '#ffffff', '#111827'] },
              ]}
            >
              <div style={{
                position: 'absolute', bottom: -8, right: -8, width: 26, height: 26, borderRadius: 13,
                background: skin.ring, border: '2px solid #fff',
                display: 'flex', alignItems: 'center', justifyContent: 'center',
                boxShadow: '0 2px 4px rgba(0,0,0,0.4)', cursor: 'pointer',
              }} onPointerDown={(e) => e.stopPropagation()}>
                <BgColorsOutlined style={{ fontSize: 13, color: '#fff' }} />
              </div>
            </ColorPicker>
          </>
        )}
      </div>
      <div style={{
        marginTop: 5, background: 'rgba(0,0,0,0.6)', color: '#fff',
        padding: '2px 8px', borderRadius: 8, fontSize: 11, fontWeight: 700, maxWidth: 90,
        overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap',
        border: `1px solid ${skin.ring}30`,
      }}>{name}</div>
    </div>
  );
  // Sem editMode, clique no bubble abre swap.
  return editMode ? bubble : <div onClick={onSwap}>{bubble}</div>;
}

// Caixa de texto ancorada em % do campo. contentEditable pra edicao inline.
// Drag por pointerdown; edit acontece quando clica sem arrastar (moved=false
// no ref). Delete quando fica vazio no blur.
function TextItem({ data, autoFocus, onFocused, onChange, onDelete, onDragStart, editable }) {
  const ref = useRef(null);
  const [hover, setHover] = useState(false);

  useEffect(() => {
    if (!autoFocus || !ref.current) return;
    ref.current.focus();
    // seleciona tudo caso ja tenha texto
    const range = document.createRange();
    range.selectNodeContents(ref.current);
    const sel = window.getSelection();
    sel.removeAllRanges(); sel.addRange(range);
    onFocused?.();
  }, [autoFocus, onFocused]);

  return (
    <div
      onPointerEnter={() => setHover(true)}
      onPointerLeave={() => setHover(false)}
      onPointerDown={(e) => {
        if (!editable) return;
        // se clicou direto no texto (contentEditable), nao arrasta — deixa
        // digitar. Drag so no wrapper.
        if (e.target === ref.current) return;
        onDragStart?.(e);
      }}
      style={{
        position: 'absolute', left: `${data.x}%`, top: `${data.y}%`,
        transform: 'translate(-50%, -50%)', zIndex: 4,
        display: 'flex', alignItems: 'center', gap: 4,
        padding: '4px 8px', borderRadius: 6,
        background: 'rgba(0,0,0,0.55)',
        border: hover && editable ? '1px dashed rgba(255,255,255,0.5)' : '1px solid transparent',
        cursor: editable ? 'grab' : 'default',
        touchAction: 'none', userSelect: 'text',
      }}
    >
      <div
        ref={ref}
        contentEditable={editable}
        suppressContentEditableWarning
        onBlur={(e) => {
          const t = e.currentTarget.innerText.trim();
          if (!t) return onDelete?.();
          if (t !== data.text) onChange?.({ text: t });
        }}
        onKeyDown={(e) => {
          if (e.key === 'Enter') { e.preventDefault(); e.currentTarget.blur(); }
          if (e.key === 'Escape') { e.currentTarget.blur(); }
        }}
        style={{
          color: data.color, fontSize: data.size, fontWeight: 700,
          outline: 'none', minWidth: 20, textShadow: '0 1px 2px rgba(0,0,0,0.6)',
          whiteSpace: 'nowrap',
        }}
      >{data.text}</div>
      {editable && hover && (
        <div
          onClick={(e) => { e.stopPropagation(); onDelete?.(); }}
          onPointerDown={(e) => e.stopPropagation()}
          style={{
            width: 22, height: 22, borderRadius: 11, background: '#ef4444',
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            cursor: 'pointer',
          }}
          title="Remover texto">
          <CloseOutlined style={{ fontSize: 12, color: '#fff' }} />
        </div>
      )}
    </div>
  );
}

function BubbleAction({ children, onClick, title, pos = 'tl' }) {
  const p = {
    tl: { top: -8, left: -8 },
    tr: { top: -8, right: -8 },
  }[pos] || { top: -8, left: -8 };
  return (
    <Tooltip title={title}>
      <div
        onClick={(e) => { e.stopPropagation(); onClick?.(); }}
        onPointerDown={(e) => e.stopPropagation()}
        style={{
          position: 'absolute', ...p, width: 26, height: 26, borderRadius: 13,
          background: '#111827', border: '2px solid #fff',
          display: 'flex', alignItems: 'center', justifyContent: 'center',
          boxShadow: '0 2px 4px rgba(0,0,0,0.4)', cursor: 'pointer',
        }}>{children}</div>
    </Tooltip>
  );
}

// Converte um hex arbitrario (do color picker) num "skin" com gradient.
function colorToSkin(hex) {
  // Simplificado: usa o hex direto como mid, cria tons claro/escuro.
  const [r, g, b] = hexToRgb(hex);
  const hi = rgbToHex(lighten(r, g, b, 0.55));
  const lo = rgbToHex(darken(r, g, b, 0.5));
  return { hi, mid: hex, lo, ring: hex };
}
function hexToRgb(hex) {
  const h = hex.replace('#', '');
  const bigint = parseInt(h.length === 3 ? h.split('').map(c => c + c).join('') : h, 16);
  return [(bigint >> 16) & 255, (bigint >> 8) & 255, bigint & 255];
}
function lighten(r, g, b, amt) {
  return [Math.round(r + (255 - r) * amt), Math.round(g + (255 - g) * amt), Math.round(b + (255 - b) * amt)];
}
function darken(r, g, b, amt) {
  return [Math.round(r * (1 - amt)), Math.round(g * (1 - amt)), Math.round(b * (1 - amt))];
}
function rgbToHex([r, g, b]) {
  return '#' + [r, g, b].map(v => v.toString(16).padStart(2, '0')).join('');
}

// Determina cor de texto de contraste (branco/preto) pra bg colorido do
// botao da caneta — usa luminancia simples.
function contrastText(hex) {
  try {
    const [r, g, b] = hexToRgb(hex);
    const lum = (0.299 * r + 0.587 * g + 0.114 * b) / 255;
    return lum > 0.6 ? '#111827' : '#ffffff';
  } catch { return '#ffffff'; }
}

// Paleta identidade Futezap — nao usar o classico laranja-gol/azul-zag/
// roxo-meia/verde-ata do FIFA card genérico. Aqui vai:
//  - gol: dourado vulcao (destaque, uma so pessoa)
//  - zag/fixo: bordo profundo (tom "muralha")
//  - ala: turquesa (velocidade)
//  - meia: verde-lima Futezap (BRAND, o coracao do time)
//  - pivo/ata: coral-fogo (perigo no gol)
const ROLE_STYLE = {
  gol:  { hi: '#fde68a', mid: '#f59e0b', lo: '#78350f', ring: '#fbbf24' }, // dourado
  zag:  { hi: '#fecaca', mid: '#dc2626', lo: '#450a0a', ring: '#ef4444' }, // bordo
  fixo: { hi: '#fecaca', mid: '#dc2626', lo: '#450a0a', ring: '#ef4444' },
  ala:  { hi: '#a5f3fc', mid: '#0891b2', lo: '#083344', ring: '#22d3ee' }, // turquesa
  meia: { hi: '#bbf7d0', mid: '#25D366', lo: '#0b3d24', ring: '#4ade80' }, // brand
  pivo: { hi: '#fed7aa', mid: '#f97316', lo: '#7c2d12', ring: '#fb923c' }, // coral
  ata:  { hi: '#fed7aa', mid: '#f97316', lo: '#7c2d12', ring: '#fb923c' },
};

// Fundo do campo por tipo:
//  - salao: piso de quadra (madeira clara com veio horizontal simulando
//    tabuas), tom quente. Marcacoes brancas grossas.
//  - society: grama sintetica uniforme com trama diagonal sutil.
//  - campo: grama natural com listras horizontais alternadas classicas.
const SURFACE = {
  salao: {
    bg: 'linear-gradient(180deg,#7c3e12 0%,#a05a1e 40%,#8a4a17 100%)',
    lineOpacity: 0.85,
  },
  society: {
    bg: 'linear-gradient(180deg,#0d5a2e 0%,#0a4022 100%)',
    lineOpacity: 0.55,
  },
  campo: {
    bg: 'linear-gradient(180deg,#0f6a34 0%,#0b1f1a 100%)',
    lineOpacity: 0.55,
  },
};

function FieldSurface({ type }) {
  if (type === 'salao') {
    // Tabuas horizontais + linha central de piso
    return (
      <svg viewBox="0 0 100 130" preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}>
        <defs>
          <pattern id="wood" x="0" y="0" width="100" height="5" patternUnits="userSpaceOnUse">
            <rect width="100" height="5" fill="none" />
            <line x1="0" y1="0" x2="100" y2="0" stroke="rgba(0,0,0,0.14)" strokeWidth="0.35" />
            <line x1="0" y1="2.5" x2="100" y2="2.5" stroke="rgba(255,255,255,0.05)" strokeWidth="0.2" />
          </pattern>
        </defs>
        <rect x="0" y="0" width="100" height="130" fill="url(#wood)" />
      </svg>
    );
  }
  if (type === 'campo') {
    // Listras horizontais claras/escuras estilo grama mowed
    return (
      <svg viewBox="0 0 100 130" preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}>
        {Array.from({ length: 13 }, (_, i) => (
          <rect key={i} x="0" y={i * 10} width="100" height="10"
            fill={i % 2 === 0 ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.08)'} />
        ))}
      </svg>
    );
  }
  // society: grama sintetica com pontos sutis
  return (
    <svg viewBox="0 0 100 130" preserveAspectRatio="none"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 0, pointerEvents: 'none' }}>
      <defs>
        <pattern id="turf" x="0" y="0" width="4" height="4" patternUnits="userSpaceOnUse">
          <circle cx="2" cy="2" r="0.3" fill="rgba(255,255,255,0.08)" />
        </pattern>
      </defs>
      <rect x="0" y="0" width="100" height="130" fill="url(#turf)" />
    </svg>
  );
}

function FieldLines({ type = 'society' }) {
  const stroke = `rgba(255,255,255,${SURFACE[type]?.lineOpacity || 0.55})`;
  // Salao: quadra 40x20 estilo futsal. Retangulos menores atras do gol,
  // area em meia-lua desenhada como arc; sem grande area/pequena area de
  // futebol de campo. Campo/society mantem area 16 + pequena area classicas.
  const sw = type === 'salao' ? 0.7 : 0.5;
  if (type === 'salao') {
    return (
      <svg viewBox="0 0 100 130" preserveAspectRatio="none"
        style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none' }}>
        <rect x="6" y="8" width="88" height="114" fill="none" stroke={stroke} strokeWidth={sw} />
        <line x1="6" y1="65" x2="94" y2="65" stroke={stroke} strokeWidth={sw} />
        <circle cx="50" cy="65" r="9" fill="none" stroke={stroke} strokeWidth={sw} />
        <circle cx="50" cy="65" r="0.6" fill={stroke} />
        {/* Marcacoes de gol topo (meia-lua) — path arc */}
        <path d={`M 30 8 A 22 22 0 0 0 70 8`} fill="none" stroke={stroke} strokeWidth={sw} />
        <line x1="42" y1="8" x2="58" y2="8" stroke={stroke} strokeWidth={sw + 0.4} />
        <path d={`M 30 122 A 22 22 0 0 1 70 122`} fill="none" stroke={stroke} strokeWidth={sw} />
        <line x1="42" y1="122" x2="58" y2="122" stroke={stroke} strokeWidth={sw + 0.4} />
      </svg>
    );
  }
  return (
    <svg viewBox="0 0 100 130" preserveAspectRatio="none"
      style={{ position: 'absolute', inset: 0, width: '100%', height: '100%', zIndex: 1, pointerEvents: 'none' }}>
      <rect x="4" y="6" width="92" height="118" fill="none" stroke={stroke} strokeWidth={sw} />
      <line x1="4" y1="65" x2="96" y2="65" stroke={stroke} strokeWidth={sw} />
      <circle cx="50" cy="65" r="9" fill="none" stroke={stroke} strokeWidth={sw} />
      <circle cx="50" cy="65" r="0.6" fill={stroke} />
      {/* Grande área topo */}
      <rect x="24" y="6" width="52" height="16" fill="none" stroke={stroke} strokeWidth={sw} />
      <rect x="36" y="6" width="28" height="7" fill="none" stroke={stroke} strokeWidth={sw} />
      {/* Grande área baixo */}
      <rect x="24" y="108" width="52" height="16" fill="none" stroke={stroke} strokeWidth={sw} />
      <rect x="36" y="117" width="28" height="7" fill="none" stroke={stroke} strokeWidth={sw} />
    </svg>
  );
}
